import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy,
  OnInit, Output, ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { AngularMaterialModule } from 'src/app/shared/module/angular-material.module';
import { AssetFault } from 'src/app/models/assetFault';
import { AssetFaultService } from 'src/app/services/asset-fault.service';
import { FingerprintCatalogEntry, IGNORED_FINGERPRINT } from 'src/app/models/fingerprintCatalogEntry';
import {
  FingerprintCatalogService,
} from 'src/app/diagnostics/component/alert-rule-detail/fingerprint-catalog.service';
import { IssuesService } from '../issues.service';
import { IssueDto } from '../models/issue';
import {
  ISSUE_REPORT_MAX_DESCRIPTION, ISSUE_REPORT_MAX_FAULTS, ISSUE_REPORT_MAX_SEVERITY,
  ISSUE_REPORT_MIN_SEVERITY, IssueCreateRequest,
} from '../models/issue-create-request';

export interface ReportIssueResult {
  requestId: string;
  mergedIntoIssueId: string | null;
}

// The slug is the value that travels to the server and is never rendered.
interface SystemGroupVm {
  category: string;
  entries: FingerprintCatalogEntry[];
}

interface FaultOptionVm {
  fault: AssetFault;
  selected: boolean;
  label: string;
  detail: string;
}

// The endpoint takes 1–100, but 0 is the one value the domain rejects ("unset"), so the
// picker offers bands. `css` is the ramp class the value lands on after thresholds apply.
const SEVERITY_BANDS: { value: number; css: string; labelKey: string }[] = [
  { value: 10, css: 'sev-low', labelKey: '@low' },
  { value: 35, css: 'sev-medium', labelKey: '@medium' },
  { value: 60, css: 'sev-high', labelKey: '@high' },
  { value: 85, css: 'sev-critical', labelKey: '@critical' },
];

// Operator issue report body. POST /v1/{tenantId}/issues answers 202 (applied
// asynchronously) or 400 with a list of messages. Hosted by the report dialog and
// inline in the alert triage dialog.
@Component({
  selector: 'app-report-issue-form',
  templateUrl: './report-issue-form.component.html',
  styleUrl: './report-issue-form.component.scss',
  standalone: true,
  imports: [CommonModule, FormsModule, AngularMaterialModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportIssueFormComponent implements OnInit, OnDestroy {
  @Input() assetId = '';
  @Input() assetName = '';
  @Input() assetVin = '';
  // The vehicle's open issues; consolidation is matched against these client-side.
  @Input() openIssues: IssueDto[] = [];
  @Input() showVehicle = true;
  // A dialog host renders its own buttons in mat-dialog-actions and drives submit()/cancel().
  @Input() showActions = true;

  @Output() reported = new EventEmitter<ReportIssueResult>();
  @Output() cancelled = new EventEmitter<void>();

  readonly severityBands = SEVERITY_BANDS;
  readonly maxDescription = ISSUE_REPORT_MAX_DESCRIPTION;
  readonly maxFaults = ISSUE_REPORT_MAX_FAULTS;

  summary = '';
  details = '';
  severity: number | null = null;

  catalogLoading = false;
  catalogError = false;
  systemGroups: SystemGroupVm[] = [];
  selectedSystem: FingerprintCatalogEntry | null = null;
  // The raw typed string until an option is chosen, the entry itself afterwards.
  systemModel: FingerprintCatalogEntry | string = '';

  faultsLoading = false;
  faultOptions: FaultOptionVm[] = [];
  faultsOpen = false;

  submitting = false;
  errors: string[] = [];
  @ViewChild('errorList') private errorList?: ElementRef<HTMLElement>;

  // The open issue this report joins, when one already exists for the chosen fingerprint.
  mergeTarget: IssueDto | null = null;

  private catalog: FingerprintCatalogEntry[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly issues: IssuesService,
    private readonly fingerprintCatalog: FingerprintCatalogService,
    private readonly assetFaults: AssetFaultService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadCatalog();
    this.loadFaults();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedFaultCount(): number {
    return this.faultOptions.filter(f => f.selected).length;
  }

  get detailsRemaining(): number {
    return this.maxDescription - (this.details?.length ?? 0);
  }

  get submitLabelKey(): string {
    return this.mergeTarget ? '@issueReportAddToIssue' : '@issueReportSubmit';
  }

  private loadCatalog(): void {
    this.catalogLoading = true;
    this.fingerprintCatalog.list().pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: entries => {
        // The ignored sentinel is a real catalog row; the server refuses a report against it.
        this.catalog = (entries ?? []).filter(
          e => !!e?.slug && !!e?.displayName && e.slug !== IGNORED_FINGERPRINT);
        this.systemGroups = this.groupEntries(this.catalog);
        this.catalogLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.catalogError = true;
        this.catalogLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  // Typing invalidates any prior choice: a half-typed name is not a selection.
  onSystemInput(value: string | FingerprintCatalogEntry): void {
    this.systemModel = value;
    if (typeof value !== 'string') return;
    this.selectedSystem = null;
    this.mergeTarget = null;
    this.systemGroups = this.groupEntries(this.filterEntries(value));
  }

  onSystemSelected(entry: FingerprintCatalogEntry): void {
    this.selectedSystem = entry;
    this.mergeTarget = this.findMergeTarget(entry);
    this.cdr.markForCheck();
  }

  displaySystem = (entry: FingerprintCatalogEntry | string | null): string =>
    typeof entry === 'string' ? entry : (entry?.displayName ?? '');

  private filterEntries(query: string): FingerprintCatalogEntry[] {
    const needle = (query ?? '').trim().toLowerCase();
    if (!needle) return this.catalog;
    return this.catalog.filter(e => (e.displayName ?? '').toLowerCase().includes(needle));
  }

  private groupEntries(entries: FingerprintCatalogEntry[]): SystemGroupVm[] {
    const byCategory = new Map<string, FingerprintCatalogEntry[]>();
    for (const entry of entries) {
      const category = entry.category || this.translate.instant('@issueReportUncategorized');
      const bucket = byCategory.get(category);
      if (bucket) bucket.push(entry); else byCategory.set(category, [entry]);
    }
    return [...byCategory.entries()]
      .map(([category, group]) => ({
        category,
        entries: [...group].sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '')),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }

  // An issue's `fingerprint` is the slug it was minted under, so a match here is the same
  // merge the writer will perform.
  private findMergeTarget(entry: FingerprintCatalogEntry): IssueDto | null {
    return (this.openIssues ?? []).find(i => i.fingerprint === entry.slug) ?? null;
  }

  private loadFaults(): void {
    if (!this.assetVin) return;
    this.faultsLoading = true;
    this.assetFaults.getAssetFaultState(this.assetVin)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: faults => {
          this.faultOptions = (faults ?? []).map(f => ({
            fault: f,
            selected: false,
            label: f.description || this.translate.instant('@issueReportUnnamedFault'),
            detail: `${f.protocol} · ${f.faultCode}/${f.failureMode}`,
          }));
          this.faultsLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.faultOptions = [];
          this.faultsLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  toggleFaults(): void { this.faultsOpen = !this.faultsOpen; }

  toggleFault(option: FaultOptionVm): void {
    if (!option.selected && this.selectedFaultCount >= this.maxFaults) return;
    option.selected = !option.selected;
  }

  submit(): void {
    if (this.submitting) return;

    const errors = this.validate();
    if (errors.length) {
      this.errors = errors;
      this.showErrors();
      return;
    }

    const requestId = uuidv4();
    const body: IssueCreateRequest = {
      requestId,
      assetId: this.assetId,
      slug: this.selectedSystem.slug,
      title: this.summary.trim(),
      description: this.details.trim(),
      severity: this.severity,
      faults: this.faultOptions.filter(f => f.selected).map(f => ({
        protocol: f.fault.protocol ?? '',
        faultCode: f.fault.faultCode ?? 0,
        failureMode: f.fault.failureMode ?? 0,
        description: f.fault.description ?? '',
        observedCount: f.fault.occurrenceCount ?? 0,
      })),
    };

    this.errors = [];
    this.submitting = true;
    this.cdr.markForCheck();
    this.issues.requestCreate(body).pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: () => this.reported.emit({ requestId, mergedIntoIssueId: this.mergeTarget?.id ?? null }),
      error: err => {
        this.errors = this.extractErrors(err);
        this.submitting = false;
        this.showErrors();
      },
    });
  }

  cancel(): void { this.cancelled.emit(); }

  // On a form taller than its host the list renders below the fold, so a refused submit
  // would look ignored. detectChanges first so the *ngIf list exists to scroll to.
  private showErrors(): void {
    this.cdr.detectChanges();
    this.errorList?.nativeElement.scrollIntoView({ block: 'nearest' });
  }

  // Mirrors the endpoint's own checks so a preventable 400 never reaches the user.
  private validate(): string[] {
    const errors: string[] = [];
    if (!this.assetId) errors.push(this.translate.instant('@issueReportErrorVehicle'));
    if (!this.selectedSystem?.slug) errors.push(this.translate.instant('@issueReportErrorProblem'));
    if (!this.summary.trim()) errors.push(this.translate.instant('@issueReportErrorSummary'));
    if (!this.details.trim()) {
      errors.push(this.translate.instant('@issueReportErrorDetails'));
    } else if (this.details.trim().length > this.maxDescription) {
      errors.push(this.translate.instant('@issueReportErrorDetailsLength', { max: this.maxDescription }));
    }
    if (this.severity == null
      || this.severity < ISSUE_REPORT_MIN_SEVERITY
      || this.severity > ISSUE_REPORT_MAX_SEVERITY) {
      errors.push(this.translate.instant('@issueReportErrorSeverity'));
    }
    if (this.selectedFaultCount > this.maxFaults) {
      errors.push(this.translate.instant('@issueReportErrorFaultCount', { max: this.maxFaults }));
    }
    return errors;
  }

  // A 400 from this endpoint is a string[] of operator-readable messages.
  private extractErrors(err: any): string[] {
    const body = err?.error;
    if (Array.isArray(body) && body.length) return body.filter(m => typeof m === 'string' && m);
    if (typeof body === 'string' && body) return [body];
    return [this.translate.instant('@issueWriteFailed')];
  }
}
